import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import Typography from '@mui/material/Typography';

import { paths } from 'src/routes/paths';
import { RouterLink } from 'src/routes/components';

import { fDate } from 'src/utils/format-time';
import { fCurrencyRupees } from 'src/utils/format-number';

import { RHFCheckbox } from 'src/components/hook-form';

import { IBadDebtMatch, BadDebtMatchField } from 'src/types/loan-agreement';

const MATCH_LABEL: Record<BadDebtMatchField, string> = {
  employee: 'same employee',
  nic: 'same NIC',
  phone: 'same phone number',
  name: 'same name',
};

type Props = {
  matches: IBadDebtMatch[];
  isAdmin: boolean;
};

/**
 * Warns that the borrower already has a bad-debt agreement. Regular users
 * are blocked (the form disables itself); admins can continue after
 * ticking the acknowledgement, which is recorded on the agreement.
 */
export default function LoanBadDebtAlert({ matches, isAdmin }: Props) {
  if (matches.length === 0) {
    return null;
  }

  const nameOnly = matches.every((match) => match.matchedOn.every((field) => field === 'name'));

  return (
    <Alert severity={isAdmin ? 'warning' : 'error'} sx={{ '& .MuiAlert-message': { width: 1 } }}>
      <AlertTitle>This person has a loan agreement marked as bad debt</AlertTitle>

      <Stack spacing={1} sx={{ mb: 1 }}>
        {matches.map((match) => (
          <Typography key={match._id} variant="body2">
            <Link
              component={RouterLink}
              href={paths.dashboard.loans.view(match._id)}
              target="_blank"
            >
              {match.borrowerName}
              {match.employeeNumber ? ` (${match.employeeNumber})` : ''}
            </Link>
            {' — '}
            {fCurrencyRupees(match.outstandingInCents)} outstanding of{' '}
            {fCurrencyRupees(match.capitalInCents)}
            {match.badDebt?.markedAt ? `, marked ${fDate(match.badDebt.markedAt)}` : ''}
            {match.badDebt?.reason ? ` (${match.badDebt.reason})` : ''}
            {' · matched on '}
            {match.matchedOn.map((field) => MATCH_LABEL[field]).join(', ')}
          </Typography>
        ))}
      </Stack>

      {isAdmin ? (
        <RHFCheckbox
          name="acknowledgeBadDebt"
          label="I acknowledge the existing bad debt and want to create this agreement anyway"
        />
      ) : (
        <Typography variant="body2">
          Only an admin can create another agreement for this person.
          {nameOnly &&
            ' If this is a different person with the same name, enter their NIC or phone number.'}
        </Typography>
      )}
    </Alert>
  );
}
